import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiJson } from "../utils/api";
import { syncMaritalStatus } from "../constants/maritalStatus";

const CLIENT_KEY = "wechat_mock_client_key";

function getClientKey() {
  let key = localStorage.getItem(CLIENT_KEY);
  if (!key) {
    key =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `wx_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(CLIENT_KEY, key);
  }
  return key;
}

export function persistWechatLogin(data) {
  localStorage.setItem("user_id", String(data.user_id));
  if (data.name) localStorage.setItem("name", data.name);
  if (data.avatar) localStorage.setItem("avatar", data.avatar);
  syncMaritalStatus(data.marital_status);
  localStorage.removeItem("consultant_id");
  localStorage.removeItem("role");
  window.dispatchEvent(new Event("avatarUpdated"));
}

/**
 * 微信登录按钮（正式扫码 / 本地 Mock）
 */
export default function WeChatLoginButton({
  redirect = "/love-map",
  className = "",
  label,
  compact = false,
}) {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    apiJson("/api/auth/wechat/status")
      .then(({ res, data }) => {
        if (!cancelled && res.ok) setStatus(data);
      })
      .catch(() => {
        if (!cancelled) setStatus({ enabled: false });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (status && status.enabled === false) return null;

  const text =
    label ||
    (status?.mock
      ? compact
        ? "微信登录（体验）"
        : "微信一键登录（本地体验）"
      : compact
        ? "微信登录"
        : "微信扫码 / 一键登录");

  const onClick = async () => {
    setBusy(true);
    setError("");
    try {
      const { res, data } = await apiJson(
        `/api/auth/wechat/start?redirect=${encodeURIComponent(redirect)}`
      );
      if (!res.ok) throw new Error(data?.detail || "无法启动微信登录");

      if (data.mode === "oauth" && data.auth_url) {
        window.location.href = data.auth_url;
        return;
      }

      // Mock：本地一键登录
      const { res: r2, data: d2 } = await apiJson("/api/auth/wechat/mock-login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          client_key: getClientKey(),
          nickname: "微信体验用户",
          redirect,
        }),
      });
      if (!r2.ok) throw new Error(d2?.detail || "微信登录失败");
      persistWechatLogin(d2);
      navigate(d2.redirect || redirect || "/love-map");
      window.location.reload();
    } catch (e) {
      setError(e.message || "登录失败");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={className}>
      <button
        type="button"
        disabled={busy || status === null}
        onClick={onClick}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-full bg-[#07C160] text-white font-semibold shadow-sm hover:bg-[#06ad56] disabled:opacity-60"
      >
        <span className="text-lg leading-none">微</span>
        {busy ? "登录中…" : text}
      </button>
      {status?.mock && !compact && (
        <p className="text-[11px] text-gray-400 mt-2 text-center leading-relaxed">
          当前为本地体验登录（未配置微信 AppID）。正式上线后将改为真实扫码，与手机号注册是同一套会员。
        </p>
      )}
      {error && <p className="text-xs text-red-500 mt-2 text-center">{error}</p>}
      {!compact && (
        <p className="text-[11px] text-gray-400 mt-2 text-center">
          也可使用{" "}
          <Link to={`/login?redirect=${encodeURIComponent(redirect)}`} className="text-rose-500">
            手机号/邮箱登录
          </Link>
        </p>
      )}
    </div>
  );
}

/** 游客自测 vs 登录匹配 双路径提示 */
export function GuestMatchHint({ redirect = "/love-map", className = "" }) {
  const userId = localStorage.getItem("user_id");
  if (userId) return null;
  return (
    <div
      className={`rounded-2xl border border-amber-100 bg-amber-50/90 p-4 space-y-3 ${className}`}
    >
      <p className="text-sm font-semibold text-amber-900">两种玩法，任选</p>
      <ul className="text-xs text-amber-900/90 space-y-1.5 leading-relaxed">
        <li>
          · <span className="font-medium">只想了解自己</span>：游客可直接测完，无需登录。
        </li>
        <li>
          · <span className="font-medium">想和别人匹配</span>：建议微信登录，测完即可对接站内匹配。
        </li>
      </ul>
      <WeChatLoginButton redirect={redirect} compact />
    </div>
  );
}
